import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Switch,
  FormControlLabel,
  TextField,
  Button,
  Grid,
  Divider,
  Chip,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useForm, Controller } from 'react-hook-form';

interface ComplianceSettingsData {
  enabled_frameworks: Array<{
    name: string;
    enabled: boolean;
    configuration: any;
  }>;
  phi_detection_threshold: number;
  breach_notification_emails: string[];
  audit_retention_days: number;
  session_timeout_minutes: number;
  custom_phi_patterns: any[];
  auto_block_phi?: boolean;
}

const frameworks = [
  { name: 'HIPAA', description: 'Health Insurance Portability and Accountability Act' },
  { name: 'ISO27001', description: 'Information Security Management System' },
  { name: 'SOC2', description: 'Service Organization Control 2' },
  { name: 'GDPR', description: 'General Data Protection Regulation' },
  { name: 'NIST80053', description: 'Security and Privacy Controls' },
];

const ComplianceSettings: React.FC = () => {
  const queryClient = useQueryClient();
  const { control, handleSubmit, reset, watch } = useForm<ComplianceSettingsData>();
  const [loadingFrameworks, setLoadingFrameworks] = useState<Set<string>>(new Set());

  const { data: settings, isLoading, refetch } = useQuery({
    queryKey: ['complianceSettings'],
    queryFn: async () => {
      const response = await axios.get('/api/organizations/compliance/settings');
      console.log('🔍 Compliance settings API response:', response.data);
      console.log('🔍 Settings data:', response.data.data);
      console.log('🔍 Enabled frameworks:', response.data.data?.enabled_frameworks);
      return response.data.data;
    },
    staleTime: 0, // Always fetch fresh data
    refetchOnWindowFocus: true, // Refetch when window regains focus
  });

  const updateSettingsMutation = useMutation({
    mutationFn: async (data: Partial<ComplianceSettingsData>) => {
      const response = await axios.put('/api/organizations/compliance/settings', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['complianceSettings'] });
      toast.success('Compliance settings updated successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update settings');
    },
  });

  const toggleFrameworkMutation = useMutation({
    mutationFn: async ({ framework, enabled }: { framework: string; enabled: boolean }) => {
      const endpoint = enabled 
        ? '/api/organizations/compliance/framework/enable'
        : '/api/organizations/compliance/framework/disable';
      const response = await axios.post(endpoint, { framework });
      return { ...response.data, framework, enabled };
    },
    onSuccess: async (data, variables) => {
      // Invalidate and refetch to get fresh data from server
      await queryClient.invalidateQueries({ queryKey: ['complianceSettings'] });
      toast.success(`${variables.framework} ${variables.enabled ? 'enabled' : 'disabled'} successfully`);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update framework');
    },
  });

  const onSubmit = (data: ComplianceSettingsData) => {
    updateSettingsMutation.mutate({
      phi_detection_threshold: data.phi_detection_threshold,
      audit_retention_days: data.audit_retention_days,
      session_timeout_minutes: data.session_timeout_minutes,
      auto_block_phi: true,
    });
  };

  const handleToggleFramework = (frameworkName: string, currentlyEnabled: boolean) => {
    setLoadingFrameworks(prev => new Set(prev).add(frameworkName));
    
    toggleFrameworkMutation.mutate({
      framework: frameworkName,
      enabled: !currentlyEnabled,
    }, {
      onSettled: () => {
        setLoadingFrameworks(prev => {
          const newSet = new Set(prev);
          newSet.delete(frameworkName);
          return newSet;
        });
      }
    });
  };

  const isFrameworkEnabled = (frameworkName: string) => {
    // The API returns data in settings.data.enabled_frameworks
    const frameworks = settings?.enabled_frameworks || [];
    const framework = frameworks.find((f: any) => f.name === frameworkName);
    const isEnabled = framework?.enabled === true;
    
    console.log(`Framework ${frameworkName}:`, {
      frameworks,
      framework,
      isEnabled,
      settings
    });
    
    return isEnabled;
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Compliance Settings
      </Typography>

      <Grid container spacing={3}>
        {/* Frameworks */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                Compliance Frameworks
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Enable the compliance frameworks that apply to your organization
              </Typography>

              <Grid container spacing={2}>
                {frameworks.map((framework) => {
                  const enabled = isFrameworkEnabled(framework.name);
                  return (
                    <Grid item xs={12} key={framework.name}>
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        p={2}
                        sx={{
                          border: 1,
                          borderColor: enabled ? 'primary.main' : 'divider',
                          borderRadius: 1,
                          bgcolor: enabled ? 'primary.50' : 'background.paper',
                        }}
                      >
                        <Box>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="body1" fontWeight="bold">
                              {framework.name}
                            </Typography>
                            {enabled && <Chip label="Active" size="small" color="primary" />}
                          </Box>
                          <Typography variant="body2" color="text.secondary">
                            {framework.description}
                          </Typography>
                        </Box>
                        <Switch
                          checked={enabled}
                          onChange={() => handleToggleFramework(framework.name, enabled)}
                          disabled={loadingFrameworks.has(framework.name)}
                          color="primary"
                        />
                        {loadingFrameworks.has(framework.name) && (
                          <CircularProgress size={16} sx={{ ml: 1 }} />
                        )}
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Configuration */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                Configuration
              </Typography>

              <form onSubmit={handleSubmit(onSubmit)}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="phi_detection_threshold"
                      control={control}
                      defaultValue={settings?.phi_detection_threshold || 0.85}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="PHI Detection Threshold"
                          type="number"
                          inputProps={{ min: 0, max: 1, step: 0.05 }}
                          helperText="Confidence threshold for PHI detection (0-1)"
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Controller
                      name="session_timeout_minutes"
                      control={control}
                      defaultValue={settings?.session_timeout_minutes || 15}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Session Timeout (minutes)"
                          type="number"
                          inputProps={{ min: 5, max: 60 }}
                          helperText="Session timeout for PHI access"
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Controller
                      name="audit_retention_days"
                      control={control}
                      defaultValue={settings?.audit_retention_days || 2190}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Audit Retention (days)"
                          type="number"
                          inputProps={{ min: 365, max: 3650 }}
                          helperText="Minimum 365 days, HIPAA requires 2190 (6 years)"
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={updateSettingsMutation.isPending}
                    >
                      {updateSettingsMutation.isPending ? 'Saving...' : 'Save Settings'}
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </CardContent>
          </Card>
        </Grid>

        {/* Status */}
        <Grid item xs={12}>
          <Alert severity="info">
            Changes to compliance settings will take effect immediately. Audit logs are maintained
            for all configuration changes.
          </Alert>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ComplianceSettings;
